import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, ArrowLeft, ArrowRight, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { CapinLogo } from '@/components/brand/CapinLogo';
import { useBranding } from '@/lib/branding';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';
import { isPasswordStrong, PASSWORD_POLICY_MESSAGE } from '@/lib/passwordPolicy';

export default function ForgotPassword() {
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);

  const requestOtp = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setInfo(data); setStep(2);
      if (data.dev_mode && data.dev_otp) toast.success(`OTP (dev): ${data.dev_otp}`, { duration: 12000 });
      else toast.success('If that email exists, an OTP has been sent.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(newPwd)) { toast.error(PASSWORD_POLICY_MESSAGE); return; }
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { email: email.trim().toLowerCase(), otp, new_password: newPwd });
      toast.success('Password reset. Please sign in.');
      navigate('/login');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-dvh grid place-items-center p-5" style={{ background: 'linear-gradient(135deg, #f7f9fc 0%, #eaf0fa 100%)' }}>
      <Card className="w-full max-w-md p-6 sm:p-8 rounded-2xl border-0 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <CapinLogo size={42} monogram={branding.logo_monogram} imageUrl={branding.logo_url} />
          <div className="text-lg font-semibold">{branding.brand_name} <span style={{ color: '#d4a017' }}>{branding.brand_suffix}</span></div>
        </div>
        <Link to="/login" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-3"><ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in</Link>
        <h2 className="text-2xl font-semibold tracking-tight text-[#0a2540]">Reset your password</h2>
        {step === 1 ? (
          <form onSubmit={requestOtp} className="mt-5 space-y-4">
            <p className="text-sm text-slate-500">Enter your account email and we’ll send a 6-digit code.</p>
            <div className="space-y-1.5">
              <Label className="text-[#0a2540] font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-9 h-11 rounded-xl bg-slate-50/60 border-slate-200" data-testid="forgot-email-input" />
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full h-12 rounded-xl text-white font-semibold" style={{ background: 'linear-gradient(180deg, #11325c 0%, #0a2540 100%)' }} data-testid="forgot-send-otp">
              {busy ? 'Sending…' : (<>Send code <ArrowRight className="ml-1 h-4 w-4" /></>)}
            </Button>
          </form>
        ) : (
          <form onSubmit={reset} className="mt-5 space-y-4">
            <div className="text-sm text-slate-500">Enter the 6-digit code sent to <b>{email}</b> and your new password.</div>
            <div className="space-y-1.5">
              <Label className="text-[#0a2540] font-medium">One-time code</Label>
              <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="••••••" className="h-12 text-center text-2xl tracking-[12px] font-mono rounded-xl" data-testid="reset-otp-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#0a2540] font-medium">New password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input type="password" required value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="At least 8 characters" className="pl-9 h-11 rounded-xl bg-slate-50/60 border-slate-200" data-testid="new-password-input" />
              </div>
            </div>
            <PasswordRequirements value={newPwd} />
            {info?.dev_mode && info?.dev_otp && <div className="text-xs rounded-lg border bg-slate-50 p-2 text-slate-600"><b>Dev mode</b> · OTP: <span className="font-mono">{info.dev_otp}</span></div>}
            <Button type="submit" disabled={busy || otp.length !== 6} className="w-full h-12 rounded-xl text-white font-semibold" style={{ background: 'linear-gradient(180deg, #11325c 0%, #0a2540 100%)' }} data-testid="reset-submit">
              {busy ? 'Resetting…' : (<>Reset password <KeyRound className="ml-1 h-4 w-4" /></>)}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
